import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Grid,
    Typography,
    CircularProgress,
    Alert,
    Switch,
    FormControlLabel,
} from '@mui/material';
import { departureDateService } from '../../../services';
import { useToast } from '../../../contexts/ToastContext';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const DepartureDateFormDialog = ({
    open,
    onClose,
    departureDate,
    isEditing,
    packages,
    onSave,
}) => {
    const [formData, setFormData] = useState({
        package_id: '',
        departure_date: null,
        return_date: null,
        is_active: true,
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const { showToast } = useToast();

    // Initialize form data when departure date changes
    useEffect(() => {
        if (departureDate && isEditing) {
            setFormData({
                package_id: departureDate.package_id || '',
                departure_date: departureDate.departure_date
                    ? new Date(departureDate.departure_date)
                    : null,
                return_date: departureDate.return_date
                    ? new Date(departureDate.return_date)
                    : null,
                is_active: departureDate.is_active ? true : false,
            });
        } else if (!isEditing) {
            // Reset form for new departure date
            setFormData({
                package_id: '',
                departure_date: null,
                return_date: null,
                is_active: true,
            });
        }
        setError('');
    }, [departureDate, isEditing]);

    // Handle form field changes
    const handleChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));

        // If package changes and we have departure date, calculate return date based on package duration
        if (field === 'package_id' && formData.departure_date) {
            const selectedPackage = packages.find((pkg) => pkg.id === value);
            if (selectedPackage && selectedPackage.duration) {
                // Parse duration like "8D6N" to get total days
                const durationMatch = selectedPackage.duration.match(/(\d+)D/);
                if (durationMatch) {
                    const totalDays = parseInt(durationMatch[1]);
                    const returnDate = new Date(formData.departure_date);
                    returnDate.setDate(returnDate.getDate() + totalDays);
                    setFormData((prev) => ({
                        ...prev,
                        return_date: returnDate,
                    }));
                }
            }
        }

        // If departure date changes and we have a package, calculate return date
        if (field === 'departure_date' && value && formData.package_id) {
            const selectedPackage = packages.find(
                (pkg) => pkg.id === formData.package_id
            );
            if (selectedPackage && selectedPackage.duration) {
                const durationMatch = selectedPackage.duration.match(/(\d+)D/);
                if (durationMatch) {
                    const totalDays = parseInt(durationMatch[1]);
                    const returnDate = new Date(value);
                    returnDate.setDate(returnDate.getDate() + totalDays);
                    setFormData((prev) => ({
                        ...prev,
                        return_date: returnDate,
                    }));
                }
            }
        }
    };

    // Calculate duration in days
    const calculateDuration = () => {
        if (!formData.departure_date || !formData.return_date) return 0;
        const diffTime = Math.abs(
            new Date(formData.return_date) - new Date(formData.departure_date)
        );
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    };

    // Validate form data
    const validateForm = () => {
        if (!formData.package_id) {
            throw new Error('Package is required');
        }
        if (!formData.departure_date) {
            throw new Error('Departure date is required');
        }
        if (!formData.return_date) {
            throw new Error('Return date is required');
        }
        if (
            new Date(formData.departure_date) >= new Date(formData.return_date)
        ) {
            throw new Error('Return date must be after departure date');
        }
        if (
            new Date(formData.departure_date) < new Date().setHours(0, 0, 0, 0)
        ) {
            throw new Error('Departure date cannot be in the past');
        }

        const duration = calculateDuration();
        if (duration < 1) {
            throw new Error('Trip duration must be at least 1 day');
        }
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            validateForm();
            function formatDateForAPI(date) {
                // Convert to YYYY-MM-DD format to avoid timezone issues
                const d = new Date(date);
                const year = d.getFullYear();
                const month = String(d.getMonth() + 1).padStart(2, '0');
                const day = String(d.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            }

            // Prepare data for API
            const submitData = {
                package_id: parseInt(formData.package_id),
                departure_date: formatDateForAPI(formData.departure_date),
                return_date: formatDateForAPI(formData.return_date),
                is_active: formData.is_active,
            };

            // Create or update departure date
            if (isEditing) {
                await departureDateService.updateDepartureDate(
                    departureDate.id,
                    submitData
                );
                showToast('Departure date updated successfully', 'success');
            } else {
                await departureDateService.createDepartureDate(submitData);
                showToast('Departure date created successfully', 'success');
            }

            onSave();
        } catch (error) {
            console.error('Error saving departure date:', error);
            const errorMessage = error.response?.data?.error?.message || 
                               error.response?.data?.message || 
                               error.message || 
                               'Failed to save departure date';
            setError(errorMessage);
            showToast('Error saving departure date: ' + errorMessage, 'error');
        } finally {
            setLoading(false);
        }
    };

    // Handle dialog close
    const handleClose = () => {
        if (!loading) {
            onClose();
            setError('');
        }
    };

    // Get selected package info
    const selectedPackage = packages.find(
        (pkg) => pkg.id === formData.package_id
    );

    return (
        <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
            <form onSubmit={handleSubmit}>
                <DialogTitle>
                    {isEditing
                        ? `Edit Departure Date`
                        : 'Create New Departure Date'}
                </DialogTitle>

                <DialogContent dividers>
                    {error && (
                        <Alert severity='error' sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Grid container spacing={3}>
                        {/* Package Selection */}
                        <Grid item xs={12}>
                            <FormControl fullWidth required>
                                <InputLabel>Package</InputLabel>
                                <Select
                                    value={formData.package_id}
                                    onChange={(e) =>
                                        handleChange(
                                            'package_id',
                                            e.target.value
                                        )
                                    }
                                    label='Package'
                                    disabled={loading}
                                >
                                    {packages.map((pkg) => (
                                        <MenuItem key={pkg.id} value={pkg.id}>
                                            {pkg.name} - {pkg.category_name} (
                                            {pkg.duration})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Package Information Display */}
                        {selectedPackage && (
                            <Grid item xs={12}>
                                <Box
                                    sx={{
                                        p: 2,
                                        bgcolor: 'grey.50',
                                        borderRadius: 1,
                                    }}
                                >
                                    <Typography
                                        variant='subtitle2'
                                        gutterBottom
                                    >
                                        Package Information:
                                    </Typography>
                                    <Grid container spacing={1}>
                                        <Grid item xs={6}>
                                            <Typography variant='body2'>
                                                <strong>Duration:</strong>{' '}
                                                {selectedPackage.duration}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant='body2'>
                                                <strong>Category:</strong>{' '}
                                                {selectedPackage.category_name}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant='body2'>
                                                <strong>Description:</strong>{' '}
                                                {selectedPackage.description?.substring(
                                                    0,
                                                    100
                                                )}
                                                ...
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>
                        )}

                        {/* Dates Section */}
                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label='Departure Date'
                                    required
                                    value={formData.departure_date}
                                    onChange={(date) => 
                                        handleChange('departure_date', date)
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            required
                                        />
                                    )}
                                    // get tommorow date
                                    minDate={new Date(Date.now() + 24 * 60 * 60 * 1000)}
                                />
                            </LocalizationProvider>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label='Return Date'
                                    required
                                    value={formData.return_date}
                                    onChange={(date) =>
                                        handleChange('return_date', date)
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            required
                                        />
                                    )}
                                    minDate={
                                        formData.departure_date || new Date()
                                    }
                                />
                            </LocalizationProvider>
                        </Grid>

                        {/* Duration Display */}
                        {formData.departure_date && formData.return_date && (
                            <Grid item xs={12}>
                                <Box
                                    sx={{
                                        p: 2,
                                        bgcolor: 'primary.50',
                                        borderRadius: 1,
                                    }}
                                >
                                    <Typography variant='body1' align='center'>
                                        <strong>Trip Duration:</strong>{' '}
                                        {calculateDuration()} days
                                    </Typography>
                                    <Typography
                                        variant='body2'
                                        align='center'
                                        color='textSecondary'
                                    >
                                        From{' '}
                                        {formData.departure_date.toLocaleDateString()}{' '}
                                        to{' '}
                                        {formData.return_date.toLocaleDateString()}
                                    </Typography>
                                </Box>
                            </Grid>
                        )}

                        {/* Settings */}
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.is_active}
                                        onChange={(e) =>
                                            handleChange(
                                                'is_active',
                                                e.target.checked
                                            )
                                        }
                                        disabled={loading}
                                    />
                                }
                                label='Active (available for booking)'
                            />
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        type='submit'
                        variant='contained'
                        disabled={loading}
                        startIcon={loading && <CircularProgress size={20} />}
                    >
                        {loading
                            ? 'Saving...'
                            : isEditing
                            ? 'Update Departure Date'
                            : 'Create Departure Date'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default DepartureDateFormDialog;
